import { Injectable } from '@angular/core'
import { Observable } from 'rxjs/Observable'
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http'
import { environment } from '../../../environments/environment'
import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from '@angular/router'
import { DocumentModel } from '../models/document.model'
import { DocumentResponse } from '../models/document-response'
import { BehaviorSubject } from 'rxjs/BehaviorSubject'
import { AdministrationService } from './administration.service'
import { SettingsService } from './settings.service'
import { ZipDownload } from '../models/zip-download'

@Injectable()
export class DocumentsService implements Resolve<Observable<any>> {
  private readonly url = environment.base_url + '/v2/support/documents'
  private readonly exportDocumentsUrl = environment.base_url + '/v2/support/administrations'
  private readonly _old_url = environment.base_url + '/v1'

  private document: DocumentModel
  private documents: DocumentResponse

  public filters: any

  documentsChanged: BehaviorSubject<DocumentResponse> = new BehaviorSubject<DocumentResponse>(undefined)
  documentChanged: BehaviorSubject<DocumentModel> = new BehaviorSubject<DocumentModel>(undefined)

  constructor(
    private _httpClient: HttpClient,
    private _administrationService: AdministrationService,
    private _administrationSettingsService: SettingsService,
  ) {
    this.filters = {
      paginated: true,
      limit: 10,
      sort: 'date',
      direction: 'DESC',
    }
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
    const documentId = route.params['documentId']
    if (documentId) {
      return this.read(documentId)
        .filter(document => !!document)
        .switchMap(document => {
          return this._administrationSettingsService.get(this.document.administration.id)
        })
        .map(adminSettings => {
          this.document.administration.setSettings(adminSettings)
          return this.document
        })
    } else {
      return this.browse()
    }
  }

  public filter(key, value) {
    if (-1 == value) {
      delete this.filters[key]
    } else {
      this.filters[key] = value
    }

    this.browse().subscribe()
  }

  /**
   * Merge g
   * @param filters
   */
  public mergeFilters(filters: any) {
    Object.assign(this.filters, this.filters, filters)

    this.browse().subscribe()
  }

  /**
   * Get a list of administrations
   * @returns {Observable<DocumentResponse>}
   */
  public browse() {
    return this._httpClient
      .get<DocumentResponse>(this.url, { params: this.filters })
      .map(response => {
        this.documents = new DocumentResponse(response)
        this.documentsChanged.next(response)
        return response
      })
  }

  /**
   * Get a list of administrations
   * @returns {Observable<DocumentResponse>}
   */
  public browseByAdministration(administrationId: number) {
    return this._httpClient.get<DocumentResponse>(`${this._old_url}/administration/${administrationId}/document`, {
      params: this.filters,
    })
  }

  /**
   *
   * @param id
   * @returns {Observable<DocumentModel>}
   */
  public read(id): Observable<DocumentModel> {
    return this._httpClient.get<DocumentModel>(`${this.url}/${id}`).map(response => {
      this.document = new DocumentModel(response)
      this.documentChanged.next(this.document)
      return this.document
    })
  }

  /**
   *
   * @param id
   * @returns {Observable<DocumentModel>}
   */
  public process(id): Observable<DocumentModel> {
    return this._httpClient.post<DocumentModel>(`${this.url}/${id}/approve`, {})
  }

  /**
   *
   * @param id
   * @returns {Observable<DocumentModel>}
   */
  public reject(id): Observable<DocumentModel> {
    return this._httpClient.post<DocumentModel>(`${this.url}/${id}/reject`, {})
  }

  /**
   *
   * @param doc
   * @param original
   */
  public download(doc: DocumentModel, original: boolean = false): Observable<Blob> {
    if (original) {
      return this._httpClient.get(
        `${this._old_url}/administration/${doc.administration.id}/document/${doc.id}/download`,
        { responseType: 'blob' },
      )
    } else {
      return this._httpClient.get(
        `${this._old_url}/administration/${doc.administration.id}/document/${doc.id}/download?original=false`,
        { responseType: 'blob' },
      )
    }
  }

  public downloadMultiple(administrationId: number, startDate: string, endDate: string): Observable<ZipDownload> {
    const params = new HttpParams().set('start_date', startDate).set('end_date', endDate)

    return this._httpClient
      .get(`${this.exportDocumentsUrl}/${administrationId}/export/documents`, {
        params,
        responseType: 'arraybuffer',
        observe: 'response',
      })
      .map((res: HttpResponse<any>) => {
        if (res.body) {
          const contentDisposition: string = res.headers.get('content-disposition'),
            filename: string = contentDisposition ? contentDisposition.match(/filename="(.+)"/)[1] : ''

          return new ZipDownload(filename, res.body)
        }
      })
  }
}



// WEBPACK FOOTER //
// ./src/app/main/services/documents.service.ts